import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchProducts } from "../../core/redux/slices/productSlice";
import { getStockMovementReport } from "../../core/api/reportService";
import { exportToCsv } from "../../core/utils/exportCsv";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];

// No default date range here (unlike other reports): this is a historical ledger, and
// the API has no server-side date filter, so defaulting to "today" would hide almost
// every record. Dates are optional, client-side narrowing only.
const DEFAULT_FILTERS = { from: "", to: "", productId: "" };

const MOVE_TYPE_BADGE = {
  "Purchase": "success", "Sale": "danger", "Stock Adjustment": "warning",
  "Opening Stock": "info", "Purchase Return": "secondary", "Sale Return": "secondary",
};

// The /api/reports/stock-movement endpoint only accepts productId — no date range params.
// Date filtering is therefore applied client-side against the returned movementDate below.
const StockMovement = () => {
  const dispatch = useDispatch();
  const products = useSelector((s) => s.products.list);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  const productOptions = [{ value: "", label: "All Products" }, ...products.map((p) => ({ value: p.id, label: p.itemName }))];

  useEffect(() => { dispatch(fetchProducts()); }, [dispatch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getStockMovementReport({ productId: f.productId });
      const payload = res.data || {};
      setAllData(payload.records || payload || []);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const data = useMemo(() => allData.filter((r) => {
    const d = r.date ? r.date.split("T")[0] : null;
    if (!d) return true;
    if (filters.from && d < filters.from) return false;
    if (filters.to && d > filters.to) return false;
    return true;
  }), [allData, filters.from, filters.to]);

  const handleExport = () => {
    const headers = ["Date", "Product", "Type", "Ref / Source", "In Qty", "Out Qty"];
    const dataRows = data.map((r) => [
      r.date ? r.date.split("T")[0] : "", r.itemName, r.type, r.referenceNo, r.inQty, r.outQty,
    ]);
    exportToCsv(`Stock-Movement-Report-${today()}.csv`, headers, dataRows);
  };

  const columns = [
    { title: "Date", dataIndex: "date", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Product", dataIndex: "itemName" },
    {
      title: "Type", dataIndex: "type", width: 130,
      render: (v) => <span className={`badge bg-${MOVE_TYPE_BADGE[v] || "secondary"}`}>{v}</span>,
    },
    { title: "Ref / Source", dataIndex: "referenceNo", ellipsis: true },
    { title: "In Qty", dataIndex: "inQty", align: "center", width: 90, render: (v) => v ? <span className="text-success fw-semibold">{v}</span> : "—" },
    { title: "Out Qty", dataIndex: "outQty", align: "center", width: 90, render: (v) => v ? <span className="text-danger fw-semibold">{v}</span> : "—" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Stock Movement Report</h4><h6>In/Out movements for each product</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col" style={{ minWidth: 220, maxWidth: 300 }}>
                <label className="form-label small mb-1">Product</label>
                <Select classNamePrefix="react-select" options={productOptions}
                  value={productOptions.find((o) => o.value && o.value === filters.productId) || null}
                  placeholder="All Products"
                  isClearable
                  onChange={(opt) => setFilters((f) => ({ ...f, productId: opt?.value || "" }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={!data.length}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockMovement;
